import React from "react";
import { createUseStyles } from "react-jss";
import { Container } from "../Container/Container";
import { getBackgroundPositionByFocusPoint } from "../helpers/helpers";
import { GridHelper } from "./GridHelper/GridHelper";
import {
  SectionGridSize,
  SectionMinHeights,
  SectionTemplates,
  VerticalAlign,
} from "../helpers/enums";
import { useScrollytellState } from "../Admin/scrollytellContext";
import { LockBlock } from "../lock/LockBlock";
import { Blocks } from "../Block/Blocks";
import { LockContainer } from "../lock/LockContainer";
import { EditContainer } from "../Container/EditContainer";
import { EditLockContainer } from "../Container/EditLockContainer";
import { EditLockBlock } from "../lock/EditLockBlock";
import { BackgroundImage } from "../background/BackgroundImage";
import { BackgroundVideo } from "../background/BackgroundVideo";
import { useContentHeight } from "../map/useContentHeight";
import {
  nineColumnsGrid,
  twelveColumnsGrid,
  thumbnailGrid,
  calculateMinHeight,
} from "./sectionGridStyles";

const useStyles = createUseStyles({
  grid: {
    top: 0,
    position: "relative",
    minHeight: ({ minHeight }) => minHeight,
    height: ({ height }) => height,
    display: "grid",
    placeItems: ({ verticalAlign }) => {
      if (verticalAlign === VerticalAlign.BOTTOM) {
        return "end stretch";
      } else if (verticalAlign === VerticalAlign.CENTER) {
        return "center stretch";
      } else {
        return "start stretch";
      }
    },
  },
  thumbnail: thumbnailGrid,
  nineColumns: nineColumnsGrid,
  twelveColumns: twelveColumnsGrid,
});

export const ContentSection = ({
  sectionId,
  isAdminMode,
  section: {
    backgroundColor,
    gradient,
    height,
    backgroundImage,
    backgroundVideo,
    containers,
    gridSize = SectionGridSize.NINE_COLUMNS,
    verticalAlign,
    backgroundFocus,
    lock,
    minHeight = SectionMinHeights.FULL,
    templateId,
  },
  isThumbnail,
}) => {
  const { showGrid } = useScrollytellState();
  const { backgroundPositionX, backgroundPositionY } =
    getBackgroundPositionByFocusPoint(backgroundFocus?.x, backgroundFocus?.y);
  const contentHeight = useContentHeight();

  const classes = useStyles({
    gradient,
    backgroundColor,
    backgroundImage,
    height,
    minHeight: calculateMinHeight({ minHeight, contentHeight }),
    verticalAlign,
    backgroundPositionX,
    backgroundPositionY,
    gridSize,
  });

  const hasLock = Boolean(
    containers?.find((container) =>
      container.blocks?.find((block) => block.locked === true)
    )
  );

  return (
    <>
      {showGrid && isAdminMode && (
        <GridHelper
          sectionId={sectionId}
          gridSize={gridSize}
          backgroundColor={backgroundColor}
        />
      )}
      {backgroundVideo && templateId === SectionTemplates.TITLE_SECTION ? (
        <BackgroundVideo videoUrl={backgroundVideo} gradient={gradient} />
      ) : (
        <BackgroundImage
          backgroundImage={backgroundImage}
          positionX={backgroundPositionX}
          positionY={backgroundPositionY}
          gradient={gradient}
          priority={sectionId === 0}
        />
      )}
      <article
        className={`${classes.grid} ${classes[gridSize]} ${
          isThumbnail ? classes.thumbnail : ""
        }`}
      >
        {containers?.map((container, i) => (
          <Container
            key={`container-${i}`}
            sectionId={sectionId}
            containerId={i}
            isAdminMode={isAdminMode}
            gridSize={gridSize}
            backgroundColor={container.backgroundColor}
            startColumn={container.startColumn}
            endColumn={container.endColumn}
            horizontalAlign={container.horizontalAlign}
            verticalAlign={container.verticalAlign}
            borderRadius={container.borderRadius}
            hasBlocks={Boolean(container.blocks?.length)}
            isThumbnail={isThumbnail}
          >
            <EditContainer
              sectionId={sectionId}
              containerId={i}
              isAdminMode={isAdminMode}
              gridSize={gridSize}
              container={container}
            />
            <Blocks
              sectionId={sectionId}
              containerId={i}
              isAdminMode={isAdminMode}
              blocks={container.blocks}
            />
          </Container>
        ))}
        <LockContainer hasLock={hasLock}>
          <Container
            key={`lock`}
            sectionId={sectionId}
            containerId={"lock"}
            isAdminMode={isAdminMode}
            gridSize={gridSize}
            backgroundColor={lock?.backgroundColor}
            startColumn={lock?.startColumn}
            endColumn={lock?.endColumn}
            horizontalAlign={lock?.horizontalAlign}
            verticalAlign={lock?.verticalAlign}
            borderRadius={lock?.borderRadius}
            hasBlocks={true}
            isThumbnail={isThumbnail}
          >
            <EditLockContainer
              sectionId={sectionId}
              containerId={"lock"}
              isAdminMode={isAdminMode}
              gridSize={gridSize}
              container={lock}
            />
            <EditLockBlock
              lock={lock}
              sectionId={sectionId}
              isAdminMode={isAdminMode}
            >
              <LockBlock
                lock={lock}
                backgroundColor={lock?.backgroundColor}
                sectionId={sectionId}
              />
            </EditLockBlock>
          </Container>
        </LockContainer>
      </article>
    </>
  );
};
